package dev.lingvo.site.components.editor

import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.ReaffirmTranslationRequest
import dev.lingvo.shared.SourceSnapshotDto
import dev.lingvo.shared.SourceChangeDto
import dev.lingvo.shared.TranslationDto
import dev.lingvo.shared.TranslationState
import dev.lingvo.shared.isCanonicalSourceReviewId
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.components.source.validCurrentSource
import dev.lingvo.site.components.source.validSourceSnapshot
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.t

internal fun sourceBoundMode(draft: EditorDraft): Boolean =
    Session.serverFeatures.sourceAwareReview || draft.sourceClaimCaptured || draft.sourceSnapshot != null

internal fun EditorDraft.hasTargetClaim(): Boolean =
    if (targetPresent) isCanonicalSourceReviewId(targetRevision.orEmpty()) else targetRevision == null

internal fun TranslationEditorState.targetReviewNeeded(keyId: String, draft: EditorDraft): Boolean =
    sourceReview.loaded(keyId)?.target?.let { isCanonicalSourceReviewId(it.revision.orEmpty()) && it.revision != draft.targetRevision } == true

/** A no-op is an acknowledged save, not evidence that its older source basis was reaffirmed. */
internal fun guardedSaveReceiptMatches(
    keyId: String,
    locale: String,
    draft: EditorDraft,
    requestedState: TranslationState,
    saved: TranslationDto,
): Boolean {
    val view = draft.sourceSnapshot?.view ?: return false
    if (saved.keyId != keyId || saved.locale != locale || !isCanonicalSourceReviewId(saved.revision.orEmpty()) || saved.state != requestedState) return false
    if (saved.value != draft.value.takeUnless { view.isPlural } || saved.pluralForms != draft.pluralForms.takeIf { view.isPlural }) return false
    if (saved.sourceRevisionId == view.sourceRevisionId) return true
    return unchangedTargetReceipt(draft, saved) || (locale == view.sourceLocale && isCanonicalSourceReviewId(saved.sourceRevisionId.orEmpty()))
}

internal fun unchangedTargetReceipt(draft: EditorDraft, saved: TranslationDto): Boolean {
    val base = draft.baseTarget
    return base != null && base.revision == draft.targetRevision && saved.revision == base.revision &&
        base.value == saved.value && base.pluralForms == saved.pluralForms && base.state == saved.state &&
        base.sourceRevisionId == saved.sourceRevisionId && base.origin == saved.origin && base.mtProvider == saved.mtProvider &&
        base.reviewStatus == saved.reviewStatus && base.reviewNote == saved.reviewNote
}

internal fun TranslationEditorState.currentSourceFor(row: KeyRow): SourceSnapshotDto? {
    val loaded = sourceReview.loaded(row.key.id)
    if (canonicalAcknowledgements.containsKey(row.key.id) && loaded == null) return null
    if (loaded != null && loaded.validCurrentSource() == null) return null
    val returned = loaded?.validCurrentSource()
    val listed = row.validSourceSnapshot()
    return when {
        returned == null -> listed
        listed == null -> returned
        returned.view.sourceVersion > listed.view.sourceVersion -> returned
        returned.view.sourceVersion < listed.view.sourceVersion -> listed
        returned.view == listed.view -> returned
        else -> null
    }
}

internal fun TranslationEditorState.sourceSaveProblem(keyId: String): String? {
    val draft = drafts[keyId] ?: return null
    val row = rowFor(keyId) ?: return unavailableSourceReviewMessage()
    if (draftShapeChanged(row, draft)) return tr("editor.editor_source_actions.the_drafts_fields_no_longer_match", "The draft's fields no longer match this key. Copy your complete draft before explicitly starting over.")
    if (!sourceBoundMode(draft)) return null
    if (!Session.serverFeatures.sourceAwareReview) return unavailableSourceReviewMessage()
    sourceReview.pauses[keyId]?.let { return it }
    if (sourceReview.targetAbsent[keyId] == true) return tr("editor.editor_source_actions.the_saved_translation_was_removed_your", "The saved translation was removed. Your draft is kept; choose explicitly whether to recreate it.")
    val current = currentSourceFor(row) ?: return unavailableSourceReviewMessage()
    if (locale == current.view.sourceLocale && !canEditStructure) return tr(
        "editor.editor_source_actions.this_language_is_now_canonical_source",
        "This language is now canonical source. An owner or admin must make source changes.",
    )
    val basis = draft.sourceSnapshot ?: return unavailableSourceReviewMessage()
    if (basis.view.keyId != keyId || !draft.hasTargetClaim()) {
        return unavailableSourceReviewMessage()
    }
    if (basis.view != current.view) return tr(
        "editor.editor_source_actions.the_source_changed_your_draft_is",
        "The source changed. Your draft is kept; review the current source before continuing.",
    )
    if (targetReviewNeeded(keyId, draft)) return tr("editor.editor_source_actions.the_saved_translation_changed_your_draft", "The saved translation changed. Your draft is kept; compare the complete saved translation before continuing.")
    return null
}

internal fun TranslationEditorState.draftShapeChanged(row: KeyRow, draft: EditorDraft): Boolean =
    draft.dirty && when (draft.retainedShape()) {
        EditorDraftShape.MIXED -> true
        EditorDraftShape.SCALAR -> currentSourceFor(row)?.view?.isPlural ?: row.key.isPlural
        EditorDraftShape.PLURAL -> !(currentSourceFor(row)?.view?.isPlural ?: row.key.isPlural)
        EditorDraftShape.UNKNOWN -> false
    }

/** A retained draft uses its own shape until an explicit discard, never a silent conversion. */
internal fun TranslationEditorState.rowForDraft(row: KeyRow, draft: EditorDraft): KeyRow {
    if (!draftShapeChanged(row, draft)) return row
    val snapshot = draft.sourceSnapshot
    return row.copy(
        key = row.key.copy(isPlural = draft.retainedShape() == EditorDraftShape.PLURAL),
        source = if (snapshot == null) row.source else row.source.copy(locale = snapshot.view.sourceLocale, value = snapshot.value, pluralForms = snapshot.pluralForms),
        sourceSnapshot = snapshot,
    )
}

internal fun TranslationEditorState.reviewCurrentSource(keyId: String): Boolean {
    if (!canWriteTranslations || !Session.serverFeatures.sourceAwareReview) return false
    val row = rowFor(keyId) ?: return false
    val draft = drafts[keyId] ?: return false
    if (draftShapeChanged(row, draft)) return false
    val change = sourceReview.loaded(keyId) ?: return false
    val snapshot = change.validCurrentSource() ?: return false
    if (snapshot != currentSourceFor(row)) return false
    if (locale == snapshot.view.sourceLocale && !canEditStructure) return false
    val target = change.target
    if (target != null && !isCanonicalSourceReviewId(target.revision.orEmpty())) return false
    val targetClaimKnown = draft.sourceSnapshot != null && draft.hasTargetClaim()
    val rebound = draft.copy(
        sourceSnapshot = snapshot,
        sourceClaimCaptured = true,
        textShape = if (snapshot.view.isPlural) EditorDraftShape.PLURAL else EditorDraftShape.SCALAR,
        targetRevision = if (targetClaimKnown || sourceReview.targetAbsent[keyId] == true) draft.targetRevision else target?.revision,
        targetPresent = if (targetClaimKnown || sourceReview.targetAbsent[keyId] == true) draft.targetPresent else target != null,
        targetUpdatedAt = if (targetClaimKnown || sourceReview.targetAbsent[keyId] == true) draft.targetUpdatedAt else target?.updatedAt,
        baseTarget = if (targetClaimKnown || sourceReview.targetAbsent[keyId] == true) draft.baseTarget else target,
        saving = false,
    )
    replaceRow(row.copy(
        key = row.key.copy(isPlural = snapshot.view.isPlural),
        source = row.source.copy(locale = snapshot.view.sourceLocale, value = snapshot.value, pluralForms = snapshot.pluralForms),
        sourceSnapshot = snapshot, target = if (sourceReview.targetAbsent[keyId] == true) row.target else target,
    ))
    drafts[keyId] = rebound
    persistDraft(keyId, rebound)
    sourceReview.pauses.remove(keyId)
    sourceReview.pauseCodes.remove(keyId)
    pauseSourceAutosave(keyId)
    if (targetClaimKnown && target?.revision != draft.targetRevision && target != null) {
        conflicts[keyId] = EditorConflict(keyId, rebound, target)
        focusSourceProblem(keyId)
    }
    return true
}

internal fun TranslationEditorState.chooseTargetRecreation(keyId: String) {
    if (!canWriteTranslations || !Session.serverFeatures.sourceAwareReview || sourceReview.targetAbsent[keyId] != true) return
    if (sourceReview.loaded(keyId)?.target != null) return
    val draft = drafts[keyId] ?: return
    drafts[keyId] = draft.copy(targetRevision = null, targetPresent = false, targetUpdatedAt = null, baseTarget = null, dirty = true)
    markTargetAbsent(keyId)
    sourceReview.targetAbsent.remove(keyId)
    if (sourceReview.pauseCodes[keyId] in listOf("stale_translation", "translation_conflict")) {
        sourceReview.pauses.remove(keyId); sourceReview.pauseCodes.remove(keyId)
    }
    pauseSourceAutosave(keyId)
    persistDraft(keyId, drafts.getValue(keyId))
}

internal fun TranslationEditorState.discardDraftForCurrentSource(keyId: String) {
    if (!canWriteTranslations || !Session.serverFeatures.sourceAwareReview) return
    val row = rowFor(keyId) ?: return
    val change = sourceReview.loaded(keyId) ?: return
    val current = change.validCurrentSource() ?: return
    if (current != currentSourceFor(row)) return
    if (change.target?.let { !isCanonicalSourceReviewId(it.revision.orEmpty()) } == true) return
    val updated = row.copy(key = row.key.copy(isPlural = current.view.isPlural),
        source = row.source.copy(locale = current.view.sourceLocale, value = current.value, pluralForms = current.pluralForms),
        sourceSnapshot = current, target = change.target)
    replaceRow(updated)
    pauseSourceAutosave(keyId)
    sourceReview.pauses.remove(keyId)
    sourceReview.pauseCodes.remove(keyId)
    sourceReview.targetAbsent.remove(keyId)
    conflicts.remove(keyId)
    recoveredDrafts.remove(keyId)
    drafts[keyId] = initialDraft(updated, locale)
    persistDraft(keyId, drafts.getValue(keyId))
}

internal fun TranslationEditorState.canReaffirm(row: KeyRow, draft: EditorDraft): Boolean {
    if (!Session.serverFeatures.sourceAwareReview || !canWriteTranslations || draft.dirty || draft.saving) return false
    if (sourceReview.pauses[row.key.id] != null) return false
    val change = sourceReview.loaded(row.key.id) ?: return false
    val current = change.validCurrentSource() ?: return false
    if (current != currentSourceFor(row) || locale == current.view.sourceLocale) return false
    val target = change.target ?: return false
    if (!isCanonicalSourceReviewId(target.revision.orEmpty()) || target.state == TranslationState.EMPTY) return false
    if (current.view.isPlural != (target.pluralForms != null) || (current.view.isPlural && target.value != null)) return false
    if (target.value.isNullOrBlank() && target.pluralForms.orEmpty().values.none(String::isNotBlank)) return false
    if (!target.stale && target.sourceRevisionId == current.view.sourceRevisionId) return false
    return target.state != TranslationState.APPROVED || canApproveTranslations
}

/** A successful own canonical edit can start a new clean draft only from an exact confirming read. */
internal fun TranslationEditorState.retainCanonicalAcknowledgement(keyId: String) {
    val (sent, saved) = canonicalAcknowledgements[keyId] ?: return
    val latest = drafts[keyId] ?: return
    if (latest.targetRevision != saved.revision || latest.sourceSnapshot?.view != sent.sourceSnapshot?.view) return
    drafts[keyId] = latest.copy(dirty = true)
    persistDraft(keyId, drafts.getValue(keyId))
    pauseSourceAutosave(keyId)
}

internal fun TranslationEditorState.acceptCanonicalAcknowledgement(
    keyId: String,
    sent: EditorDraft,
    saved: TranslationDto,
    change: SourceChangeDto,
) {
    val latest = drafts[keyId] ?: return
    val row = rowFor(keyId) ?: return
    if (latest.dirty || latest.saving || latest.targetRevision != saved.revision ||
        latest.sourceSnapshot?.view != sent.sourceSnapshot?.view
    ) return
    val current = change.validCurrentSource()
    val target = change.target
    val confirmedReceipt = current != null && target != null && target.revision == saved.revision &&
        current.view.sourceRevisionId == saved.sourceRevisionId && current.view.sourceLocale == locale &&
        current.value == saved.value && current.pluralForms == saved.pluralForms &&
        target.value == saved.value && target.pluralForms == saved.pluralForms && target.state == saved.state &&
        target.sourceRevisionId == saved.sourceRevisionId &&
        (row.target?.revision == sent.targetRevision || row.target?.revision == saved.revision)
    if (!confirmedReceipt) {
        drafts[keyId] = latest.copy(dirty = true)
        persistDraft(keyId, drafts.getValue(keyId))
        pauseSourceAutosave(keyId)
        return
    }
    checkNotNull(current)
    checkNotNull(target)
    val confirmed = row.copy(key = row.key.copy(isPlural = current.view.isPlural), sourceSnapshot = current,
        source = row.source.copy(locale = current.view.sourceLocale, value = current.value, pluralForms = current.pluralForms), target = target)
    replaceRow(confirmed)
    drafts[keyId] = initialDraft(confirmed, locale).copy(sourceSnapshot = current, sourceClaimCaptured = true)
    persistDraft(keyId, drafts.getValue(keyId))
}

internal fun TranslationEditorState.retryGuardedSave(keyId: String) {
    if (!canWriteTranslations || !Session.serverFeatures.sourceAwareReview || keyId != selectedKeyId) return
    sourceReview.pauses.remove(keyId); sourceReview.pauseCodes.remove(keyId)
    saveCurrent(approve = false, advance = false)
}

internal fun TranslationEditorState.reaffirm(row: KeyRow) {
    val draft = drafts[row.key.id] ?: return
    if (!canReaffirm(row, draft)) return
    val change = sourceReview.loaded(row.key.id) ?: return
    val current = change.validCurrentSource() ?: return
    val target = change.target ?: return
    pauseSourceAutosave(row.key.id)
    sourceReview.runReaffirm(row.key.id) {
        if (!canReaffirm(row, drafts[row.key.id] ?: return@runReaffirm)) return@runReaffirm
        try {
            val saved = ApiClient.reaffirmTranslation(projectSlug, row.key.id, locale,
                ReaffirmTranslationRequest(current.view, checkNotNull(target.revision)))
            if (!isCanonicalSourceReviewId(saved.revision.orEmpty()) || saved.sourceRevisionId != current.view.sourceRevisionId ||
                saved.keyId != row.key.id || saved.locale != locale) throw unavailableSourceReview()
            val existing = rowFor(row.key.id) ?: return@runReaffirm
            val mayApply = existing.target?.revision == target.revision || existing.target?.revision == saved.revision
            if (mayApply) replaceRow(existing.copy(target = saved))
            val latest = drafts[row.key.id] ?: draft
            if (latest.targetRevision == target.revision) {
                drafts[row.key.id] = latest.copy(targetRevision = saved.revision, targetPresent = true,
                    targetUpdatedAt = saved.updatedAt, baseTarget = saved)
                persistDraft(row.key.id, drafts.getValue(row.key.id))
            }
            if (!latest.dirty && !latest.saving && mayApply) {
                val confirmed = existing.copy(
                    sourceSnapshot = current,
                    source = existing.source.copy(locale = current.view.sourceLocale, value = current.value, pluralForms = current.pluralForms),
                    target = saved,
                )
                drafts[row.key.id] = initialDraft(confirmed, locale).copy(sourceSnapshot = current, sourceClaimCaptured = true)
            }
            val evidence = sourceReview.loaded(row.key.id) ?: change
            if (evidence.target?.revision == target.revision || evidence.target?.revision == saved.revision) {
                sourceReview.changes[row.key.id] = Async.Success(evidence.copy(target = saved, earlierSource = current))
            }
            sourceCompletion(tr("editor.editor_source_actions.translation_confirmed_for_this_source", "Translation confirmed for this source."))
            panels.refreshHistory(row.key.id)
            // The typing was retained while this metadata write owned the target token.
            sourceReview.reaffirming.remove(row.key.id)
            resumeSourceAutosave(row.key.id)
        } catch (failure: ApiException) {
            sourceReview.refused(row.key.id, failure)
            pauseSourceAutosave(row.key.id)
            focusSourceProblem(row.key.id)
        } catch (cancelled: kotlinx.coroutines.CancellationException) {
            throw cancelled
        } catch (_: Throwable) {
            sourceReview.pauses[row.key.id] = unavailableSourceReviewMessage()
            focusSourceProblem(row.key.id)
        }
    }
}
